import { Button, Popover, PopoverTrigger, PopoverContent } from '@heroui/react';
import { Link2, Unlink } from 'lucide-react';
import type { DimAxis } from '@estimator/shared';

interface PanelDimControlsProps {
  /** null means the dimension is static. */
  link: DimAxis | null;
  onChange: (link: DimAxis | null) => void;
  /** Which dimension this controls, e.g. "Width" — used in the popover heading. */
  label: string;
}

const OPTIONS: Array<{ value: DimAxis | null; text: string }> = [
  { value: null, text: 'Static' },
  { value: 'width', text: 'Track component width' },
  { value: 'depth', text: 'Track component depth' },
  { value: 'height', text: 'Track component height' },
];

/**
 * Chain-icon toggle that links one dimension to a component axis.
 *
 * The numeric input beside this control keeps showing the BASE value — the
 * resolved size appears in the row's grey note, so the number you typed is
 * always the number you can edit.
 *
 * Every dimension may track every axis, so this takes no axis subset and is not
 * generic. It previously carried both, plus an "Auto (full width)" entry for
 * foam length; spanning the run is now an ordinary link to the run axis.
 */
export default function PanelDimControls({ link, onChange, label }: PanelDimControlsProps) {
  const title = link ? `${label} tracks component ${link}` : `${label} is static`;

  return (
    <Popover placement="bottom-end">
      <PopoverTrigger>
        <Button isIconOnly size="sm" variant="light" aria-label={title} title={title}>
          {link
            ? <Link2 size={14} style={{ color: 'var(--accent)' }} />
            : <Unlink size={14} style={{ color: 'var(--text-muted)' }} />}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-1 bg-[var(--bg-panel)]">
        <div className="px-2 py-1 text-[10px]" style={{ color: 'var(--text-muted)' }}>
          {label}
        </div>
        {OPTIONS.map((opt) => (
          <Button
            key={opt.value ?? 'static'}
            size="sm"
            variant={link === opt.value ? 'flat' : 'light'}
            className="w-full justify-start text-xs"
            onPress={() => onChange(opt.value)}
          >
            {opt.text}
          </Button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
